import * as uuid from 'uuid';

/**
 * Normalize string ids that are actually numeric (e.g. graphic template ids
 * stored as strings in Shows) so they match the producer's lookup tables.
 * Non-numeric ids are returned untouched.
 */
const toNumericId = (id) => {
  if (id == null) {
    return null;
  }

  const str = String(id).trim();

  return /^\d+$/.test(str) ? Number(str) : str;
};

/**
 * Convert a Show SegmentItem descriptor into a production timeline entry,
 * mirroring the shapes produced by the producer's own library tabs and the
 * "Add group"/"Add segment" buttons:
 *   - graphic  -> GraphicTemplates/SavedGraphics clone (template_id numeric,
 *                 displayTime 5000, transition 'Cut', posts self-reference)
 *   - asset    -> Design Library clone (mediaMeta, posts [], _id)
 *   - panel    -> SmartPanels clone
 *   - poll     -> Polls clone
 *   - theme    -> Themes clone
 *   - marker   -> Timeline marker
 *
 * @param {Object} item - A resolved SegmentItem (or nested child) payload.
 * @param {Object} [options]
 * @param {Number|String} [options.owner] - Business unit id.
 * @param {Boolean} [options.optimizeImages] - Whether to request image
 *        optimization on media assets (mediaMeta).
 * @param {String} [options.transition] - The production's transition, used
 *        as the default for graphics groups and segments.
 * @param {Object} [options.assets] - Lookup of asset records keyed by id,
 *        used to populate media_url/thumbnail_url/asset_type on media items.
 * @param {Object} [options.themes] - Lookup of theme records keyed by id,
 *        used to populate the item's `layout` sub-type (e.g. 'lowerthird').
 * @returns {Object} A timeline item compatible with the producer playlist.
 */
const buildTimelineItem = (item, options = {}) => {
  const {
    owner,
    optimizeImages = false,
    assets = {},
    themes = {},
  } = options;
  const source = item || {};
  const resourceType = source.resourceType || 'theme';
  const timelineId = uuid.v1();

  // Assign unique timeline_ids to social posts nested inside fields[layerId].posts
  const fields = { ...(source.fields || {}) };
  Object.keys(fields).forEach((layerId) => {
    const layer = fields[layerId];
    if (layer?.posts?.length) {
      fields[layerId] = {
        ...layer,
        posts: layer.posts.map((post) => ({
          ...post,
          timeline_id: post.timeline_id || uuid.v1(),
        })),
      };
    }
  });

  // Assign unique timeline_ids to the item-level posts array
  const mapPosts = (posts) => (posts || []).map((post) => ({
    ...post,
    timeline_id: post.timeline_id || uuid.v1(),
  }));

  switch (resourceType) {
    case 'graphic': {
      const templateId = toNumericId(source.resourceId);

      return {
        title: source.title || '',
        ...(templateId ? { template_id: templateId } : {}),
        timeline_id: timelineId,
        displayTime: source.displayTime || 5000,
        transition: source.transition || 'Cut',
        layout: 'graphic',
        posts: [{ timeline_id: timelineId }],
        fields,
        meta: source.meta || {},
        notes: source.notes || '',
        owner,
      };
    }

    case 'asset': {
      // Media items only carry their id in Shows, so resolve the asset
      // details (media_url, asset_type, ...) from the options.assets lookup
      // populated by the caller, matching the Design Library clone shape.
      const assetId = source.resourceId ?? null;
      const asset = assetId != null ? (assets || {})[assetId] : null;

      return {
        _id: assetId,
        title: asset?.title || source.title || '',
        ...(asset?.media_url ? { media_url: asset.media_url } : {}),
        ...(asset?.thumbnail_url ? { thumbnail_url: asset.thumbnail_url } : {}),
        ...(asset?.asset_type ? { asset_type: asset.asset_type } : {}),
        timeline_id: timelineId,
        displayTime: source.displayTime || 5000,
        transition: source.transition || 'Cut',
        layout: 'asset',
        posts: [],
        fields: {},
        notes: source.notes || '',
        mediaMeta: optimizeImages ? { optimize: true } : {},
      };
    }

    case 'panel':
      return {
        _id: source.resourceId ?? null,
        title: source.title || '',
        timeline_id: timelineId,
        displayTime: source.displayTime || 5000,
        transition: source.transition || 'Cut',
        layout: 'panel',
        posts: [{ timeline_id: timelineId }],
        fields: {},
        notes: source.notes || '',
        owner,
      };

    case 'poll':
    case 'display': {
      // The Show API stores poll/display ids as strings while the producer's
      // poll lookup table (and GlobalSearch-built items) use numeric ids, and
      // the lookup matches with strict equality. Normalize to numbers so the
      // display record resolves (mirrors graphic template_id handling).
      const resourceId = toNumericId(source.resourceId ?? null);

      return {
        title: source.title || '',
        pollId: toNumericId(source.pollId ?? null),
        displayId: toNumericId(source.displayId ?? resourceId),
        displayType: source.displayType ?? null,
        previewUrl: source.previewUrl ?? null,
        timeline_id: timelineId,
        displayTime: source.displayTime || 30000,
        transition: source.transition || 'Cut',
        layout: 'poll',
        posts: [],
        fields: {},
        notes: source.notes || '',
        owner,
      };
    }

    case 'marker':
      return {
        title: source.title || '',
        timeline_id: timelineId,
        layout: 'marker',
        fields,
        owner,
      };

    default: {
      // Themes (and everything else) resolve through the theme lookup by _id.
      // The theme record carries the real `layout` sub-type (e.g. 'lowerthird',
      // 'grid', ...) that the timeline renders by, so use it when available.
      const resourceId = source.resourceId ?? null;
      const theme = resourceId != null ? (themes || {})[String(resourceId)] : null;

      return {
        title: source.title || '',
        ...(resourceId ? { _id: toNumericId(resourceId) } : {}),
        timeline_id: timelineId,
        displayTime: source.displayTime || 30000,
        transition: source.transition || 'Cut',
        layout: theme?.layout || resourceType,
        posts: mapPosts(source.posts),
        fields,
        queuedPost: source.queuedPost ?? null,
        notes: source.notes || '',
        owner,
      };
    }
  }
};

/**
 * Build a timeline entry for a SegmentItem. Items with nested children
 * (e.g. graphics inside a graphic group) become a `graphics` group entry,
 * matching the timeline's own graphics-group records.
 *
 * @param {Object} item - A resolved SegmentItem payload.
 * @param {Object} [options] - See buildTimelineItem.
 * @returns {Object} A timeline entry.
 */
const buildTimelineEntry = (item, options = {}) => {
  const source = item || {};
  const nestedChildren = (source.items || []).map((child) => buildTimelineEntry(child, options));

  if (nestedChildren.length > 0) {
    return {
      title: source.title || '',
      timeline_id: uuid.v1(),
      displayTime: source.displayTime || 5000,
      transition: source.transition || options.transition || 'Cut',
      layout: 'graphics',
      notes: source.notes || '',
      posts: nestedChildren,
    };
  }

  return buildTimelineItem(source, options);
};

/**
 * Build a timeline segment entry from a Show segment's data.
 * Nested SegmentItems become the segment's `posts`.
 *
 * @param {Object} segmentData - The `data` of a ShowItem with itemType 'segment'.
 * @param {Object} [options] - See buildTimelineItem.
 * @returns {Object} A timeline entry with layout 'segment'.
 */
const buildSegmentEntry = (segmentData, options = {}) => {
  const { owner, transition } = options;
  const data = segmentData || {};

  return {
    title: data.title || '',
    timeline_id: uuid.v1(),
    displayTime: 5000,
    transition: transition || 'Cut',
    layout: 'segment',
    notes: data.notes || '',
    settings: data.settings || {},
    posts: (data.items || []).map((child) => buildTimelineEntry(child, options)),
    fields: {},
    meta: {},
    owner,
  };
};

/**
 * Flatten a Show's resolved items into timeline entries, mirroring
 * `generateProduction` in api/lib/shows.js. Segments stay segments (with
 * their children as posts), standalone items and markers become entries.
 *
 * @param {Object[]} showItems - The `items` array from GET /shows/:id.
 * @param {Object} [options] - See buildTimelineItem.
 * @returns {Object[]} Timeline entries ready for the producer playlist.
 */
const flattenShowItems = (showItems, options = {}) => (showItems || []).map((showItem) => {
  if (!showItem) {
    return null;
  }

  if (showItem.itemType === 'segment') {
    return buildSegmentEntry(showItem.data, options);
  }

  return buildTimelineEntry(showItem.data || showItem, options);
}).filter(Boolean);

/**
 * Derive the store drag type for a single ShowItem (or nested SegmentItem),
 * so existing timeline drop restrictions apply (e.g. only graphics and
 * markers can enter graphics groups; segments cannot nest into segments).
 *
 * @param {Object} item - A ShowItem envelope ({ itemType, data }) or a bare
 *                        SegmentItem payload.
 * @returns {String} A drag type understood by the producer store.
 */
const getDragTypeForItem = (item) => {
  const source = item || {};

  if (source.itemType === 'segment') {
    return 'segment';
  }

  const data = source.data || source;
  switch (data.resourceType) {
    case 'graphic':
      return 'graphic';
    case 'panel':
      return 'panel';
    case 'poll':
    case 'display':
      return 'poll';
    case 'marker':
      return 'marker';
    case 'asset':
      return 'asset';
    case 'theme':
    case 'lowerthird':
    case 'isolated':
    case 'carousel':
    case 'grid':
    case 'waterfall':
    case 'mural':
    case 'upnext':
      return 'theme';
    default:
      return 'component';
  }
};

export {
  buildTimelineItem,
  buildTimelineEntry,
  buildSegmentEntry,
  flattenShowItems,
  getDragTypeForItem,
};
